import { useState } from "react";
import { C } from "./re-colors.ts";
import {
  TEST_DATA, BOOK_OF_BUSINESS, LENDING_PIPELINE,
  CECL_SCENARIOS, EXCEPTIONS,
} from "./re-data.ts";
import { fmt, Bdg } from "./re-components.tsx";

// ── TYPES ─────────────────────────────────────────────────────────
type FieldDef = {
  key: string;
  label: string;
  type?: "text" | "number" | "select";
  options?: string[];
  placeholder?: string;
  required?: boolean;
};

type ColDef = {
  key: string;
  label: string;
  render?: (val: any, row: any) => React.ReactNode;
};

type EntityCfg = {
  id: string;
  label: string;
  icon: string;
  description: string;
  category: string;
  columns: ColDef[];
  fields: FieldDef[];
  getData: () => any[];
};

// ── ENTITY DEFINITIONS ────────────────────────────────────────────
const ENTITIES: EntityCfg[] = [
  // ── 1. CLIENTS ────────────────────────────────────────────────────
  {
    id: "clients",
    label: "Clients",
    icon: "🏢",
    description: "Business relationships in the bank's book of business",
    category: "CRM",
    columns: [
      { key: "name",         label: "Client Name" },
      { key: "type",         label: "Loan Type" },
      { key: "industry",     label: "Industry" },
      { key: "city",         label: "City" },
      { key: "rm",           label: "RM" },
      { key: "status",       label: "Status",
        render: v => <Bdg label={v} color={v === "Watch" ? C.gold : v === "Prospect" ? C.navy : C.forest} /> },
      { key: "creditRating", label: "Rating" },
      { key: "score",        label: "Score",
        render: v => <span style={{ fontWeight: 800, color: v > 80 ? C.forest : v > 70 ? C.gold : C.burgundy }}>{v}</span> },
      { key: "risk",         label: "Risk",
        render: v => <Bdg label={v} color={v === "high" ? C.burgundy : v === "medium" ? C.gold : C.forest} /> },
    ],
    fields: [
      { key: "name",         label: "Client Name",           placeholder: "e.g. Metro Medical Partners",    required: true },
      { key: "type",         label: "Loan Type",             placeholder: "e.g. C&I, CRE, Ag LOC" },
      { key: "industry",     label: "Industry",              placeholder: "e.g. Healthcare" },
      { key: "city",         label: "City",                  placeholder: "e.g. Metro City" },
      { key: "rm",           label: "Relationship Manager",  type: "select", options: ["J. Reed", "M. Torres", "K. Perry"] },
      { key: "status",       label: "Status",                type: "select", options: ["Active", "Watch", "Prospect"] },
      { key: "creditRating", label: "Credit Rating",         placeholder: "e.g. B+" },
      { key: "score",        label: "Health Score (0–100)",  type: "number", placeholder: "75" },
      { key: "risk",         label: "Risk Level",            type: "select", options: ["low", "medium", "high"] },
    ],
    getData: () => BOOK_OF_BUSINESS,
  },

  // ── 2. LOAN PIPELINE ──────────────────────────────────────────────
  {
    id: "pipeline",
    label: "Loan Pipeline",
    icon: "📝",
    description: "Active commercial lending opportunities and their workflow stage",
    category: "Lending",
    columns: [
      { key: "id",          label: "ID" },
      { key: "borrower",    label: "Borrower" },
      { key: "type",        label: "Type" },
      { key: "amount",      label: "Amount",  render: v => fmt(v) },
      { key: "stage",       label: "Stage",
        render: v => {
          const sc: Record<string, string> = {
            Approved: C.forest, Committee: C.gold, Underwriting: C.rust,
            Processing: C.gold, "Pre-approval": C.cyan, "Term Sheet": C.cyan, "SBA Review": C.muted,
          };
          return <Bdg label={v} color={sc[v] || C.muted} />;
        }},
      { key: "rm",          label: "RM" },
      { key: "daysIn",      label: "Days In",
        render: v => <span style={{ color: v > 20 ? C.burgundy : v > 10 ? C.gold : C.muted }}>{v}d</span> },
      { key: "closeTarget", label: "Close Target" },
      { key: "issue",       label: "Blocker",
        render: v => v ? <span style={{ color: C.burgundy, fontWeight: 600 }}>⚠ {v}</span> : <span style={{ color: C.forest }}>—</span> },
    ],
    fields: [
      { key: "id",          label: "Loan ID",                placeholder: "e.g. LP-009",            required: true },
      { key: "borrower",    label: "Borrower Name",          placeholder: "e.g. Valley Tech LLC",   required: true },
      { key: "type",        label: "Loan Type",              type: "select", options: ["CRE", "C&I", "Ag LOC", "SBA 7(a)", "SBA 504", "Equipment", "C&I LOC"] },
      { key: "amount",      label: "Amount ($)",             type: "number", placeholder: "1000000" },
      { key: "stage",       label: "Stage",                  type: "select", options: ["Pre-approval", "Processing", "Underwriting", "Committee", "Term Sheet", "Approved", "SBA Review"] },
      { key: "rm",          label: "Relationship Manager",   type: "select", options: ["J. Reed", "M. Torres", "K. Perry"] },
      { key: "ltv",         label: "LTV (e.g. 0.65)",        type: "number", placeholder: "0.65" },
      { key: "rate",        label: "Rate % (e.g. 7.25)",     type: "number", placeholder: "7.25" },
      { key: "daysIn",      label: "Days in Stage",          type: "number", placeholder: "1" },
      { key: "closeTarget", label: "Close Target",           placeholder: "e.g. May 1" },
      { key: "issue",       label: "Blocking Issue",         placeholder: "Leave blank if none" },
    ],
    getData: () => LENDING_PIPELINE,
  },

  // ── 3. DEPOSIT CONCENTRATIONS ─────────────────────────────────────
  {
    id: "deposits",
    label: "Deposit Concentrations",
    icon: "🏦",
    description: "Top depositor concentration risk — FDIC and liquidity monitoring",
    category: "Funding",
    columns: [
      { key: "name",      label: "Depositor" },
      { key: "type",      label: "Business Type" },
      { key: "balance",   label: "Balance",    render: v => fmt(v) },
      { key: "pct",       label: "% of Total", render: v => `${(v * 100).toFixed(1)}%` },
      { key: "uninsured", label: "Uninsured",
        render: v => v > 0
          ? <span style={{ color: C.burgundy, fontWeight: 700 }}>{fmt(v)}</span>
          : <span style={{ color: C.forest }}>Fully insured</span> },
      { key: "risk",      label: "Risk",
        render: v => <Bdg label={v} color={v === "critical" ? C.burgundy : v === "high" ? C.gold : C.rust} /> },
    ],
    fields: [
      { key: "name",      label: "Depositor Name",                  placeholder: "e.g. Reedley USD",        required: true },
      { key: "type",      label: "Business Type",                   placeholder: "e.g. Govt / Muni" },
      { key: "balance",   label: "Balance ($)",                     type: "number", placeholder: "50000000" },
      { key: "pct",       label: "% of Total (decimal, e.g. 0.015)", type: "number", placeholder: "0.015" },
      { key: "uninsured", label: "Uninsured Amount ($)",            type: "number", placeholder: "0" },
      { key: "risk",      label: "Risk Level",                      type: "select", options: ["critical", "high", "medium", "low"] },
    ],
    getData: () => TEST_DATA.deposits.topConcentrations,
  },

  // ── 4. CRITICIZED ASSETS ──────────────────────────────────────────
  {
    id: "criticized",
    label: "Criticized Assets",
    icon: "⚠️",
    description: "Special Mention, Substandard, and Doubtful credit classifications",
    category: "Credit",
    columns: [
      { key: "name", label: "Borrower" },
      { key: "type", label: "Type" },
      { key: "bal",  label: "Balance",        render: v => fmt(v) },
      { key: "cls",  label: "Classification",
        render: v => <Bdg label={v} color={v === "Doubtful" ? C.burgundy : v === "Substandard" ? C.rust : C.gold} /> },
      { key: "risk", label: "Risk Notes" },
      { key: "due",  label: "Action Due" },
    ],
    fields: [
      { key: "name", label: "Borrower Name",     placeholder: "e.g. River County Retail LLC",        required: true },
      { key: "type", label: "Loan Type",          type: "select", options: ["CRE", "C&I", "Ag LOC", "Equipment", "Residential"] },
      { key: "bal",  label: "Balance ($)",         type: "number", placeholder: "1500000" },
      { key: "cls",  label: "Classification",      type: "select", options: ["Special Mention", "Substandard", "Doubtful", "Loss"] },
      { key: "risk", label: "Risk Notes",          placeholder: "e.g. Covenant breach · LOC 94%" },
      { key: "due",  label: "Action Due",          placeholder: "e.g. Mar 31" },
    ],
    getData: () => TEST_DATA.criticizedAssets.credits,
  },

  // ── 5. AGRICULTURAL CREDITS ───────────────────────────────────────
  {
    id: "agricultural",
    label: "Agricultural Credits",
    icon: "🌾",
    description: "Farm and crop loan portfolio with drought insurance monitoring",
    category: "Credit",
    columns: [
      { key: "name",   label: "Borrower" },
      { key: "bal",    label: "Balance",   render: v => fmt(v) },
      { key: "crop",   label: "Crop Type" },
      { key: "ins",    label: "Insurance",
        render: v => <Bdg label={v} color={v === "Verified" ? C.forest : v === "Pending" ? C.gold : C.burgundy} /> },
      { key: "action", label: "Required Action" },
    ],
    fields: [
      { key: "name",   label: "Borrower Name",     placeholder: "e.g. Lakeside Citrus Farms",    required: true },
      { key: "bal",    label: "Balance ($)",         type: "number", placeholder: "3000000" },
      { key: "crop",   label: "Crop Type",           placeholder: "e.g. Citrus, Almonds, Cotton" },
      { key: "ins",    label: "Insurance Status",    type: "select", options: ["Verified", "Not Verified", "Pending"] },
      { key: "action", label: "Required Action",     placeholder: "e.g. Request policy cert" },
    ],
    getData: () => TEST_DATA.agricultural.credits,
  },

  // ── 6. SBA LEADS ──────────────────────────────────────────────────
  {
    id: "sba",
    label: "SBA Leads",
    icon: "🏛️",
    description: "SBA pre-qualified pipeline — 7(a), 504, and Microloan programs",
    category: "Lending",
    columns: [
      { key: "name",   label: "Business Name" },
      { key: "type",   label: "Program" },
      { key: "amt",    label: "Loan Amount", render: v => fmt(v) },
      { key: "fee",    label: "Est. Fee",    render: v => fmt(v) },
      { key: "status", label: "Status",      render: v => <Bdg label={v} color={C.gold} /> },
      { key: "banker", label: "Banker" },
    ],
    fields: [
      { key: "name",   label: "Business Name",      placeholder: "e.g. Metro Physical Therapy", required: true },
      { key: "type",   label: "SBA Program",         type: "select", options: ["7(a)", "504", "Microloan", "EIDL"] },
      { key: "amt",    label: "Loan Amount ($)",      type: "number", placeholder: "500000" },
      { key: "fee",    label: "Estimated Fee ($)",    type: "number", placeholder: "15000" },
      { key: "status", label: "Status",               type: "select", options: ["Pre-Qualified", "In Process", "Approved", "Closed"] },
      { key: "banker", label: "Banker",               type: "select", options: ["J. Reed", "M. Torres", "K. Perry"] },
    ],
    getData: () => TEST_DATA.sba.leads,
  },

  // ── 7. CRM RELATIONSHIPS ──────────────────────────────────────────
  {
    id: "crm",
    label: "CRM Relationships",
    icon: "🤝",
    description: "Retention scorecard and AI-prioritized next-best-action queue",
    category: "CRM",
    columns: [
      { key: "name",   label: "Relationship" },
      { key: "type",   label: "Type" },
      { key: "loan",   label: "Loans",    render: v => fmt(v) },
      { key: "dep",    label: "Deposits", render: v => fmt(v) },
      { key: "prods",  label: "Products", render: v => `${v}` },
      { key: "score",  label: "Score",
        render: v => <span style={{ fontWeight: 800, color: v > 80 ? C.forest : v > 70 ? C.gold : C.burgundy }}>{v}</span> },
      { key: "risk",   label: "Risk",
        render: v => <Bdg label={v} color={v === "high" ? C.burgundy : v === "medium" ? C.gold : C.forest} /> },
      { key: "action", label: "Next Action" },
    ],
    fields: [
      { key: "name",   label: "Relationship Name",       placeholder: "e.g. Valley Logistics Inc.",  required: true },
      { key: "type",   label: "Loan Type",               placeholder: "e.g. C&I, CRE" },
      { key: "loan",   label: "Total Loans ($)",          type: "number", placeholder: "2000000" },
      { key: "dep",    label: "Total Deposits ($)",       type: "number", placeholder: "500000" },
      { key: "prods",  label: "# Products",              type: "number", placeholder: "3" },
      { key: "score",  label: "Retention Score (0–100)", type: "number", placeholder: "75" },
      { key: "risk",   label: "Risk Level",               type: "select", options: ["low", "medium", "high"] },
      { key: "action", label: "Next Action",              placeholder: "e.g. Annual review Q2" },
    ],
    getData: () => TEST_DATA.crm.relationships,
  },

  // ── 8. CECL SEGMENTS ──────────────────────────────────────────────
  {
    id: "cecl_segments",
    label: "CECL Segments",
    icon: "📊",
    description: "Allowance for Credit Losses (ACL) reserve broken out by loan segment",
    category: "Credit",
    columns: [
      { key: "name",  label: "Segment" },
      { key: "loans", label: "Total Loans", render: v => fmt(v) },
      { key: "acl",   label: "ACL Reserve", render: v => fmt(v) },
      { key: "rate",  label: "Reserve Rate",
        render: v => <span style={{ fontWeight: 700, color: v > 0.02 ? C.burgundy : v > 0.015 ? C.gold : C.forest }}>{(v * 100).toFixed(2)}%</span> },
    ],
    fields: [
      { key: "name",  label: "Segment Name",                       placeholder: "e.g. Consumer Installment", required: true },
      { key: "loans", label: "Total Loans ($)",                     type: "number", placeholder: "100000000" },
      { key: "acl",   label: "ACL Reserve ($)",                     type: "number", placeholder: "1500000" },
      { key: "rate",  label: "Reserve Rate (decimal, e.g. 0.015)", type: "number", placeholder: "0.015" },
    ],
    getData: () => TEST_DATA.cecl.segments,
  },

  // ── 9. CECL SCENARIOS ─────────────────────────────────────────────
  {
    id: "cecl_scenarios",
    label: "CECL Scenarios",
    icon: "🔮",
    description: "FASB ASC 326 multi-scenario macro stress test configurations",
    category: "Credit",
    columns: [
      { key: "id",            label: "ID" },
      { key: "label",         label: "Scenario" },
      { key: "gdp",           label: "GDP",
        render: v => <span style={{ color: v >= 0 ? C.forest : C.burgundy, fontWeight: 700 }}>{v >= 0 ? "+" : ""}{v}%</span> },
      { key: "unemployment",  label: "Unemployment", render: v => `${v}%` },
      { key: "hpiChange",     label: "HPI Δ",
        render: v => <span style={{ color: v >= 0 ? C.forest : C.burgundy }}>{v >= 0 ? "+" : ""}{v}%</span> },
      { key: "provision",     label: "Provision",    render: v => fmt(v) },
      { key: "aclRate",       label: "ACL Rate",     render: v => `${(v * 100).toFixed(2)}%` },
      { key: "reserveStatus", label: "Status",
        render: (v, row) => <Bdg label={v} color={row.color || (v === "Adequate" ? C.forest : v === "Stressed" ? C.burgundy : C.gold)} /> },
    ],
    fields: [
      { key: "id",            label: "Scenario ID",                       placeholder: "e.g. S6",              required: true },
      { key: "label",         label: "Scenario Label",                    placeholder: "e.g. S6 — Stagflation" },
      { key: "gdp",           label: "GDP Growth %",                      type: "number", placeholder: "-1.2" },
      { key: "unemployment",  label: "Unemployment Rate %",               type: "number", placeholder: "7.5" },
      { key: "hpiChange",     label: "HPI Change %",                      type: "number", placeholder: "-8.0" },
      { key: "provision",     label: "Provision ($)",                     type: "number", placeholder: "80000000" },
      { key: "aclRate",       label: "ACL Rate (decimal, e.g. 0.028)",   type: "number", placeholder: "0.028" },
      { key: "reserveStatus", label: "Reserve Status",                    type: "select", options: ["Adequate", "Monitor", "Stressed"] },
    ],
    getData: () => CECL_SCENARIOS,
  },

  // ── 10. EXCEPTIONS QUEUE ──────────────────────────────────────────
  {
    id: "exceptions",
    label: "Exceptions Queue",
    icon: "🔴",
    description: "ACH, wire, Positive Pay, Reg E, and OFAC exception items",
    category: "Operations",
    columns: [
      { key: "id",       label: "ID" },
      { key: "type",     label: "Type" },
      { key: "category", label: "Category" },
      { key: "customer", label: "Customer" },
      { key: "amount",   label: "Amount",   render: v => fmt(v) },
      { key: "severity", label: "Severity",
        render: v => <Bdg label={v} color={v === "critical" ? C.burgundy : v === "high" ? C.gold : v === "medium" ? C.rust : C.forest} /> },
      { key: "age",      label: "Age",      render: v => `${v}d` },
      { key: "assignee", label: "Assignee", render: v => v ?? <span style={{ color: C.dim }}>Unassigned</span> },
    ],
    fields: [
      { key: "id",       label: "Exception ID",         placeholder: "e.g. EX-W-004",             required: true },
      { key: "type",     label: "Type",                  type: "select", options: ["Wire", "ACH", "Positive Pay", "Reg E", "Override", "OFAC"] },
      { key: "category", label: "Category",              placeholder: "e.g. R01 NSF, OFAC, Check" },
      { key: "customer", label: "Customer Name",         placeholder: "e.g. Valley Medical Partners" },
      { key: "amount",   label: "Amount ($)",             type: "number", placeholder: "5000" },
      { key: "issue",    label: "Issue Description",     placeholder: "e.g. NSF — payroll return" },
      { key: "age",      label: "Age (days)",             type: "number", placeholder: "0" },
      { key: "severity", label: "Severity",               type: "select", options: ["critical", "high", "medium", "low"] },
      { key: "assignee", label: "Assignee",               placeholder: "e.g. Sandra Moore" },
    ],
    getData: () => EXCEPTIONS,
  },

  // ── 11. TRAINING MODULES ──────────────────────────────────────────
  {
    id: "training",
    label: "Training Modules",
    icon: "📚",
    description: "Compliance and operational training completion by module",
    category: "Compliance",
    columns: [
      { key: "module",   label: "Module" },
      { key: "pct",      label: "Completion",
        render: v => <span style={{ fontWeight: 700, color: v === 100 ? C.forest : v >= 75 ? C.gold : C.burgundy }}>{v}%</span> },
      { key: "deadline", label: "Deadline" },
      { key: "status",   label: "Status",
        render: v => <Bdg label={v} color={v === "Complete" ? C.forest : v === "On Track" ? C.gold : C.burgundy} /> },
    ],
    fields: [
      { key: "module",   label: "Module Name",   placeholder: "e.g. Fair Lending Deep Dive", required: true },
      { key: "pct",      label: "Completion %",  type: "number", placeholder: "0" },
      { key: "deadline", label: "Deadline",       placeholder: "e.g. Apr 30, 2026" },
      { key: "status",   label: "Status",         type: "select", options: ["On Track", "At Risk", "Complete", "Overdue"] },
    ],
    getData: () => TEST_DATA.training,
  },

  // ── 12. RATE SENSITIVITY ──────────────────────────────────────────
  {
    id: "rate_sensitivity",
    label: "Rate Sensitivity",
    icon: "📈",
    description: "NII impact under parallel interest rate shift scenarios (ALM / ALCO)",
    category: "ALM",
    columns: [
      { key: "scenario",  label: "Rate Scenario" },
      { key: "niiImpact", label: "NII Impact",
        render: v => <span style={{ fontWeight: 700, color: v >= 0 ? C.forest : C.burgundy }}>{v >= 0 ? "+" : ""}{fmt(Math.abs(v))}</span> },
      { key: "pct",       label: "% Change",
        render: v => <span style={{ color: v >= 0 ? C.forest : C.burgundy }}>{v >= 0 ? "+" : ""}{(v * 100).toFixed(1)}%</span> },
    ],
    fields: [
      { key: "scenario",  label: "Scenario Label",                   placeholder: "e.g. +300 bps",  required: true },
      { key: "niiImpact", label: "NII Impact ($, negative = loss)",  type: "number", placeholder: "12000000" },
      { key: "pct",       label: "% Change (decimal, e.g. 0.18)",   type: "number", placeholder: "0.18" },
    ],
    getData: () => TEST_DATA.alm.rateSensitivity,
  },
];

// ── CATEGORIES for sidebar grouping ──────────────────────────────
const CATEGORY_ORDER = ["CRM", "Lending", "Funding", "Credit", "Operations", "Compliance", "ALM"];

// ── TEST DATA PANEL ───────────────────────────────────────────────
export function TestDataPanel({ onClose }: { onClose: () => void }) {
  const [selectedId, setSelectedId]   = useState(ENTITIES[0].id);
  const [addedRows,  setAddedRows]    = useState<Record<string, any[]>>({});
  const [showForm,   setShowForm]     = useState(false);
  const [formVals,   setFormVals]     = useState<Record<string, string>>({});
  const [search,     setSearch]       = useState("");

  const entity   = ENTITIES.find(e => e.id === selectedId)!;
  const baseRows = entity.getData();
  const newRows  = addedRows[selectedId] || [];
  const allRows  = [...baseRows, ...newRows];

  const filtered = search.trim()
    ? allRows.filter(row =>
        Object.values(row).some(v => String(v ?? "").toLowerCase().includes(search.toLowerCase()))
      )
    : allRows;

  const totalRecords = ENTITIES.reduce(
    (sum, e) => sum + e.getData().length + (addedRows[e.id]?.length || 0), 0
  );

  function selectEntity(id: string) {
    setSelectedId(id);
    setShowForm(false);
    setFormVals({});
    setSearch("");
  }

  function resetForm() {
    setFormVals({});
    setShowForm(false);
  }

  function handleAddRow() {
    const row: any = { _isNew: true };
    entity.fields.forEach(f => {
      const raw = formVals[f.key] ?? "";
      row[f.key] = f.type === "number"
        ? (raw !== "" ? parseFloat(raw) : null)
        : raw || null;
    });
    setAddedRows(prev => ({
      ...prev,
      [selectedId]: [...(prev[selectedId] || []), row],
    }));
    resetForm();
  }

  const categories = CATEGORY_ORDER.filter(c => ENTITIES.some(e => e.category === c));

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 600,
        background: "rgba(20,15,10,.65)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "16px 24px",
      }}
    >
      <div style={{
        background: C.bg, width: "min(96vw,1320px)", height: "calc(100vh - 32px)",
        borderRadius: 16, display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(20,15,10,.5)",
        border: `1px solid ${C.border}`, overflow: "hidden",
      }}>

        {/* ── HEADER ── */}
        <div style={{ background: C.navy, padding: "14px 20px", display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>
            🗄️
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#fff", letterSpacing: -.2 }}>Test Data Explorer</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginTop: 1 }}>
              {totalRecords} records · {ENTITIES.length} entities · Demo Regional Bank · static seed data
            </div>
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", padding: "4px 10px", background: "rgba(255,255,255,.08)", borderRadius: 8, border: "1px solid rgba(255,255,255,.12)" }}>
            Session-only · changes do not persist
          </div>
          <button
            onClick={onClose}
            style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: "6px 14px", fontWeight: 700, fontSize: 12 }}
          >
            ✕ Close
          </button>
        </div>

        {/* ── BODY ── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* ── LEFT SIDEBAR ── */}
          <div style={{ width: 228, flexShrink: 0, background: C.surface, borderRight: `1px solid ${C.border}`, overflowY: "auto", padding: "10px 0 20px" }}>
            {categories.map(cat => (
              <div key={cat}>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: "uppercase", letterSpacing: 1.1, padding: "10px 16px 5px" }}>
                  {cat}
                </div>
                {ENTITIES.filter(e => e.category === cat).map(e => {
                  const count   = e.getData().length + (addedRows[e.id]?.length || 0);
                  const added   = addedRows[e.id]?.length || 0;
                  const isActive = e.id === selectedId;
                  return (
                    <button
                      key={e.id}
                      onClick={() => selectEntity(e.id)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 9,
                        padding: "8px 16px",
                        background: isActive ? `${C.navy}12` : "transparent",
                        border: "none",
                        borderLeft: `3px solid ${isActive ? C.navy : "transparent"}`,
                        cursor: "pointer", textAlign: "left",
                      }}
                    >
                      <span style={{ fontSize: 15, flexShrink: 0 }}>{e.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? C.navy : C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {e.label}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                        {added > 0 && (
                          <span style={{ fontSize: 9, background: C.navy, color: "#fff", borderRadius: 10, padding: "1px 5px", fontWeight: 700 }}>
                            +{added}
                          </span>
                        )}
                        <span style={{ fontSize: 10, color: C.dim, background: C.border, borderRadius: 10, padding: "1px 6px" }}>
                          {count}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* ── MAIN CONTENT ── */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* Entity toolbar */}
            <div style={{ padding: "13px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0, background: C.card }}>
              <span style={{ fontSize: 22 }}>{entity.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{entity.label}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{entity.description}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
                <span style={{ fontWeight: 700, color: C.text }}>{filtered.length}</span> record{filtered.length !== 1 ? "s" : ""}
                {newRows.length > 0 && (
                  <span style={{ color: C.navy, fontWeight: 700 }}>· +{newRows.length} new</span>
                )}
              </div>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter records…"
                style={{ fontSize: 12, padding: "7px 12px", border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg, color: C.text, width: 180, outline: "none" }}
              />
              <button
                onClick={() => { setShowForm(f => !f); setFormVals({}); }}
                style={{
                  background: showForm ? "transparent" : C.navy,
                  color: showForm ? C.navy : "#fff",
                  border: `1px solid ${C.navy}`,
                  borderRadius: 8, padding: "7px 16px",
                  cursor: "pointer", fontWeight: 700, fontSize: 12,
                  display: "flex", alignItems: "center", gap: 6,
                  flexShrink: 0,
                }}
              >
                {showForm ? "✕ Cancel" : "+ Add Row"}
              </button>
            </div>

            {/* Add-row form */}
            {showForm && (
              <div style={{ padding: "16px 20px", background: `${C.navy}06`, borderBottom: `1px solid ${C.navy}20`, flexShrink: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: C.navy, textTransform: "uppercase", letterSpacing: .9, marginBottom: 12 }}>
                  New {entity.label.replace(/s$/, "")} Record
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10, marginBottom: 14 }}>
                  {entity.fields.map(f => (
                    <div key={f.key}>
                      <label style={{ fontSize: 10, color: C.muted, fontWeight: 700, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: .4 }}>
                        {f.label}
                        {f.required && <span style={{ color: C.burgundy }}> *</span>}
                      </label>
                      {f.type === "select" ? (
                        <select
                          value={formVals[f.key] || ""}
                          onChange={e => setFormVals(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ width: "100%", fontSize: 12, padding: "7px 10px", border: `1px solid ${C.border}`, borderRadius: 7, background: C.card, color: C.text, outline: "none" }}
                        >
                          <option value="">Select…</option>
                          {f.options!.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input
                          type={f.type === "number" ? "number" : "text"}
                          value={formVals[f.key] || ""}
                          onChange={e => setFormVals(p => ({ ...p, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          style={{ width: "100%", fontSize: 12, padding: "7px 10px", border: `1px solid ${C.border}`, borderRadius: 7, background: C.card, color: C.text, outline: "none" }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    onClick={handleAddRow}
                    style={{ background: C.navy, color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                  >
                    ✓ Add Record
                  </button>
                  <button
                    onClick={resetForm}
                    style={{ background: "none", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <span style={{ fontSize: 11, color: C.dim, marginLeft: 4 }}>Added rows are visible here but do not affect the live dashboard.</span>
                </div>
              </div>
            )}

            {/* Data table */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: C.surface, position: "sticky", top: 0, zIndex: 5 }}>
                    {entity.columns.map(col => (
                      <th
                        key={col.key}
                        style={{
                          padding: "10px 16px", textAlign: "left",
                          fontSize: 10, fontWeight: 700, color: C.dim,
                          textTransform: "uppercase", letterSpacing: .6,
                          borderBottom: `2px solid ${C.border}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={entity.columns.length} style={{ padding: "48px 16px", textAlign: "center", color: C.dim, fontSize: 13 }}>
                        {search ? `No records match "${search}"` : "No records"}
                      </td>
                    </tr>
                  ) : filtered.map((row, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        background: row._isNew
                          ? `${C.navy}07`
                          : i % 2 === 0 ? C.card : C.surface,
                      }}
                    >
                      {entity.columns.map((col, ci) => (
                        <td
                          key={col.key}
                          style={{ padding: "11px 16px", color: C.text, verticalAlign: "middle", maxWidth: 260, wordBreak: "break-word" }}
                        >
                          {ci === 0 && row._isNew && (
                            <span style={{ fontSize: 9, background: C.navy, color: "#fff", borderRadius: 10, padding: "2px 7px", fontWeight: 800, marginRight: 7, verticalAlign: "middle" }}>
                              NEW
                            </span>
                          )}
                          {col.render
                            ? col.render(row[col.key], row)
                            : row[col.key] == null
                              ? <span style={{ color: C.dim }}>—</span>
                              : String(row[col.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Footer */}
              <div style={{ padding: "10px 16px 20px", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.dim, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  {filtered.length} of {allRows.length} record{allRows.length !== 1 ? "s" : ""}
                  {search && ` matching "${search}"`}
                  {newRows.length > 0 && !search && ` · ${newRows.length} added this session`}
                </span>
                <span>Source: {entity.id === "pipeline" || entity.id === "sba" || entity.id === "cecl_scenarios" || entity.id === "exceptions" ? "re-data.ts exported const" : "TEST_DATA · re-data.ts"}</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
